import { Component, OnInit, AfterViewInit, ViewChildren, QueryList } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Title } from '@angular/platform-browser';

import { HeaderService } from '../../services/header.service';
import { Dictionary, OfferDetails, Settings, User } from '../../models/models';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ModalInfoComponent } from '../../sections/modal-info/modal-info.component';
import { DictionaryService } from '../../services/dictionary.service';
import { ModalErrorComponent } from '../../sections/modal-error/modal-error.component';
import { ModalSaveOfferComponent } from '../../sections/modal-save-offer/modal-save-offer.component';
import { OrgDepDivSelectorComponent } from "../../components/org-dep-div-selector/org-dep-div-selector.component";
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { OfferService } from '../../services/offer.service';
import { UtilsService } from '../../services/helpers/utils.service';
import { AuthGuard } from '../../services/auth/auth.guard';
import { environment } from 'src/environments/environment';
import { forkJoin } from 'rxjs';
import { LoaderService } from 'src/app/services/helpers/loader.service';

@Component({
  selector: 'app-add-offer',
  templateUrl: './add-offer.component.html',
})
export class AddOfferComponent implements OnInit, AfterViewInit {

  @ViewChildren("orgDepDivSelector") orgDepDivSelectorQuery: QueryList<OrgDepDivSelectorComponent>;
  private _orgDepDivSelector: OrgDepDivSelectorComponent;

  redactorForm1: FormGroup;
  redactorForm2: FormGroup;
  redactorForm3: FormGroup;

  offerTypes: Dictionary[];
  rubrics: Dictionary[];
  offerLines: Dictionary[];
  allClassificators: Dictionary[] = [];
  allLossTypes: Dictionary[] = [];
  allOrganizations: Dictionary[] = [];
  allDepartments: Dictionary[] = [];
  allDivisions: Dictionary[] = [];
  allUserBlocks: Dictionary[] = [];
  selectedOrganizations: Dictionary[];
  selectedDepartments: Dictionary[];
  selectedDivisions: Dictionary[];
  selectedUserBlocks: Dictionary[];
  selectedClassificators: Dictionary[];
  selectedLossTypes: Dictionary[];
  authorMemberList: User[];
  coAuthorMemberList: User[];

  settings: Settings;
  offer: OfferDetails;
  currentStep = 1;
  offerError: string;
  offerMembersError: string;
  offerMembersPartInProjectError: string;
  offerSaveError: string;
  fileSizeError: string;
  fileTypeError: string;
  photoError: string;
  offerPublished = false;
  saveDraft = false;
  isEdit = false;

  infoOfferTypeText: string;
  infoHiddenPublishedText: string;

  user: User;

  isEditOffer: boolean = false;

  constructor(
    private title: Title,
    private translate: TranslateService,
    private headerService: HeaderService,
    private dictionaryService: DictionaryService,
    private offerService: OfferService,
    private modalService: NgbModal,
    private route: ActivatedRoute,
    private utilsService: UtilsService,
    private formBuilder: FormBuilder,
    private authGuard: AuthGuard,
    private loaderService: LoaderService,
    private router: Router
  ) {

  }

  get form1() {
    return this.redactorForm1.controls;
  }
  get form2() {
    return this.redactorForm2.controls;
  }
  get form3() {
    return this.redactorForm3.controls;
  }

  ngOnInit() {
    this.redactorForm1 = this.formBuilder.group({
      typeId: [0, Validators.required],
    });

    this.redactorForm2 = this.formBuilder.group({
      name: ['', Validators.required],
      problem: ['', Validators.required],
      reason: ['', Validators.required],
      solution: ['', Validators.required],
      outlay: [''],
      result: ['', Validators.required],
      hasEconomicEffect: [0, Validators.required],
      comment: [''],
      agreeWithSuggestionsPolicy: [false, Validators.requiredTrue],
      data1: new FormControl(null)
    });

    this.redactorForm3 = this.formBuilder.group({
      organizationId: ['', Validators.required],
      departmentId: ['', Validators.required],
      divisionId: [''],
      // TODO Добавить блок
      userBlockId: [''],
      lossTypes: [[], Validators.maxLength(0)],
      rubric: ['', Validators.required],
      classificator: ['', Validators.required],
    });

    this.authGuard.authLoggedIn$.subscribe((user) => (this.user = user));

    this.loaderService.showLoader();

    if (this.route.snapshot.params.offerId) {
      this.currentStep = 2;
    }

    forkJoin([
      this.dictionaryService.getSettings(),
      this.translate.get('Pages.AddOffer'),
    ]).subscribe(([settings, translate]) => {
      this.settings = settings;

      const memberAuthor = { ...this.user, isAuthor: true, checked: false, partInProject: 100 };

      this.offer = {
        statusId: null,
        members: [memberAuthor],
        typeId: this.settings.nornickelDefaultOfferKindId,
        hiddenPublished: false,
        lossTypeIds: [],
        organization: this.user.organization,
        department: this.user.department,
        division: this.user.division,
        organizationId: this.user.organizationId,
        departmentId: this.user.departmentId,
        divisionId: this.user.divisionId,
        photosBeforeIntroduction: [],
        photosAfterIntroduction: [],
        userBlockId: this.user.blockId,
        userBlock: this.user.block,
      } as OfferDetails;

      this.isEditOffer = typeof this.route.snapshot.data.offer?.id === 'number';

      const titleName = this.isEditOffer ? translate.TitleEdit : translate.TitleCreate;

      this.title.setTitle(titleName);
      this.headerService.setPageTitle(false, 0, titleName);

      this.offerError = translate.OfferError;
      this.offerMembersError = translate.OfferMembersError;
      this.offerMembersPartInProjectError = translate.OfferMembersPartInProjectError;
      this.offerSaveError = translate.OfferSaveError;
      this.fileSizeError = translate.FileSizeError;
      this.fileTypeError = translate.FileTypeError;
      this.photoError = translate.PhotoError;
      this.infoOfferTypeText = translate.InfoOfferTypeText;
      this.infoHiddenPublishedText = translate.infoHiddenPublishedText;

      this.loaderService.showLoader();
      forkJoin([
        this.dictionaryService.getOfferKinds(true),
        this.dictionaryService.getOrganizations(true),
        this.dictionaryService.getAddOfferDictionaries(this.user.id, true),
      ]).subscribe((res) => {
        this.offerTypes = environment.offerTypes as Dictionary[];
        this.offerLines = environment.offerLines as Dictionary[];
        this.allOrganizations = res[1];
        this.rubrics = res[2][0];
        this.allDepartments = res[2][1];
        this.allDivisions = res[2][2];
        this.allLossTypes = res[2][3];
        this.allUserBlocks = res[2][4];
        this.allClassificators = res[2][5];

        if (this.route.snapshot.data.offer) {
          this.offer = this.route.snapshot.data.offer;
        }

        if (!this.offer.members) {
          this.offer.members = [];
        }
        if (!this.offer.lossTypeIds) {
          this.offer.lossTypeIds = [];
        }
        if (!this.offer.photosBeforeIntroduction) {
          this.offer.photosBeforeIntroduction = [];
        }
        if (!this.offer.photosAfterIntroduction) {
          this.offer.photosAfterIntroduction = [];
        }
        if (!this.offer.typeId) {
          this.offer.typeId = this.offerTypes[0].value;
        }
        if (!this.offer.hasEconomicEffect) {
          this.offer.hasEconomicEffect = this.offerLines[0].value;
        }

        if (this.offer.typeId === this.settings.nornickelProblem_OfferKindId) {
          this.offer.comment = this.offer.result;
          this.offer.result = "";
        }

        this.authorMemberList = this.offer.members.filter(q => q.isAuthor);
        this.coAuthorMemberList = this.offer.members.filter(q => q.isCoAuthor);

        this.form1.typeId.setValue(this.offer.typeId);

        this.form2.name.setValue(this.offer.name);
        this.form2.problem.setValue(this.offer.problem);
        this.form2.reason.setValue(this.offer.reason);
        this.form2.solution.setValue(this.offer.solution);
        this.form2.comment.setValue(this.offer.comment);
        this.form2.outlay.setValue(this.offer.outlay);
        this.form2.result.setValue(this.offer.result);
        this.form2.hasEconomicEffect.setValue(this.offer.hasEconomicEffect);
        this.form2.agreeWithSuggestionsPolicy.setValue(this.offer.agreeWithSuggestionsPolicy);

        this.selectedOrganizations = this.allOrganizations.filter(q => q.value == this.offer.organizationId);
        if (this.selectedOrganizations)
          this.selectedOrgChangeEvent(this.selectedOrganizations);
        this.selectedDepartments = this.allDepartments.filter(q => q.value == this.offer.departmentId);
        if (this.selectedDepartments)
          this.selectedDepChangeEvent(this.selectedDepartments);
        this.selectedDivisions = this.allDivisions.filter(q => q.value == this.offer.divisionId);
        if (this.selectedDivisions)
          this.selectedDivChangeEvent(this.selectedDivisions);
        this.selectedUserBlocks = this.allUserBlocks.filter(q => q.value == this.offer.userBlockId);
        if (this.selectedUserBlocks)
          this.selectedUserBlockChangeEvent(this.selectedUserBlocks);

        if (this.rubrics?.length > 0 && this.offer.rubricIds?.length > 0) {
          const rubric = this.rubrics.find((x) => this.offer.rubricIds.includes(x.value));
          if (rubric) {
            this.form3.rubric.setValue(rubric.value);
          }
        }

        if (this.allClassificators?.length > 0 && this.offer.offerClassificatorId) {
          this.selectedClassificators = this.allClassificators.filter(q => q.value == this.offer.offerClassificatorId);
          if (this.selectedClassificators)
            this.selectedClassificatorsChangeEvent(this.selectedClassificators);
        }

        if (this.allLossTypes && this.allLossTypes.length) {
          this.selectedLossTypes = this.allLossTypes.filter(q => this.offer.lossTypeIds?.some(x => q.value == x));
          if (this.selectedLossTypes)
            this.selectedLossTypesChangeEvent(this.selectedLossTypes);
        }

        if (!this.offer.members.find((x: User) => x.id === this.authGuard.userId)?.isAuthor) {
          this.router.navigate(['/']);
        }

        if (this.offer?.data1 && this.settings.nornickelStepForward_OfferKindId) {
          this.form2.data1.setValue((this.offer?.data1));
        }

        this.isEdit = true;
        this.setValidators();

        this.loaderService.hideLoader();
      });
    });

  }

  ngAfterViewInit() {
    this.orgDepDivSelectorQuery.changes.subscribe(q => {
      this._orgDepDivSelector = q.first;
    });
  }

  public selectedLossTypesChangeEvent(arr: Dictionary[]) {
    if (arr.length) {      
      this.form3.lossTypes.setValue(arr.map(q => q.value));
    }
    else {      
      this.form3.lossTypes.setValue([]);
    }
  }

  public selectedClassificatorsChangeEvent(arr: Dictionary[]) {
    if (arr.length) {
      this.form3.classificator.setValue(arr[0].value);
    }
    else {
      this.form3.classificator.setValue('');
    }
  }

  public selectedOrgChangeEvent(arr: Dictionary[]) {
    if (arr.length) {
      this.form3.organizationId.setValue(arr[0].value);
    }
    else {
      this.form3.organizationId.setValue('');
    }
  }

  public selectedDepChangeEvent(arr: Dictionary[]) {
    if (arr.length) {
      this.form3.departmentId.setValue(arr[0].value);
    }
    else {
      this.form3.departmentId.setValue('');
    }
  }

  public selectedDivChangeEvent(arr: Dictionary[]) {
    if (arr.length) {
      this.form3.divisionId.setValue(arr[0].value);
    }
    else {
      this.form3.divisionId.setValue('');
    }
  }

  public selectedUserBlockChangeEvent(arr: Dictionary[]) {
    if (arr.length) {
      this.form3.userBlockId.setValue(arr[0].value);
    }
    else {
      this.form3.userBlockId.setValue('');
    }
  }

  public offerMemberListChangeEvent(arr: User[]) {
    this.offer.members = this.authorMemberList.concat(this.coAuthorMemberList);
  }

  changeOfferType(typeId: number) {
    this.offer.typeId = typeId;
    this.form1.typeId.setValue(this.offer.typeId);

    if (this.form1.typeId.value != '') {
      this.form2.reason.setValue('');
      this.form2.outlay.setValue('');
      this.form2.result.setValue('');

      this.setValidators();
    }
  }

  changeEconomicEffect(offerLineId: number) {
    this.offer.hasEconomicEffect = offerLineId;
    this.form2.hasEconomicEffect.setValue(this.offer.hasEconomicEffect);
  }

  setValidators() {
    this.redactorForm2.get('reason').clearValidators();
    this.redactorForm2.get('reason').updateValueAndValidity();
    this.redactorForm2.get('result').clearValidators();
    this.redactorForm2.get('result').updateValueAndValidity();
    this.redactorForm2.get('hasEconomicEffect').clearValidators();
    this.redactorForm2.get('hasEconomicEffect').updateValueAndValidity();
    this.redactorForm2.get('agreeWithSuggestionsPolicy').clearValidators();
    this.redactorForm2.get('agreeWithSuggestionsPolicy').updateValueAndValidity();

    this.redactorForm3.get('lossTypes').clearValidators();
    this.redactorForm3.get('lossTypes').updateValueAndValidity();
    this.redactorForm3.get('rubric').clearValidators();
    this.redactorForm3.get('rubric').updateValueAndValidity();
    this.redactorForm3.get('classificator').clearValidators();
    this.redactorForm3.get('classificator').updateValueAndValidity();

    this.redactorForm2.get('data1').clearValidators();
    this.redactorForm2.get('data1').updateValueAndValidity();

    this.redactorForm2.get('solution').clearValidators();
    this.redactorForm2.get('solution').updateValueAndValidity();

    if (this.form1.typeId.value == this.settings.nornickelOffer_OfferKindId) {
      this.redactorForm2.get('reason').setValidators(Validators.required);
      this.redactorForm2.get('result').setValidators(Validators.required);
      this.redactorForm2.get('hasEconomicEffect').setValidators(Validators.required);
      this.redactorForm2.get('agreeWithSuggestionsPolicy').setValidators(Validators.requiredTrue);
      this.redactorForm3.get('lossTypes').setValidators(Validators.minLength(1));
      this.redactorForm3.get('lossTypes').setValidators(Validators.required);      

      if (!this.isEditOffer)
        this.form2.hasEconomicEffect.setValue(1);
    }

    if (this.form1.typeId.value == this.settings.nornickelProblem_OfferKindId) {
      this.redactorForm3.get('classificator').setValidators(Validators.required);

      this.form2.hasEconomicEffect.setValue(null);
    }

    if (this.form1.typeId.value == this.settings.nornickelStepForward_OfferKindId) {
      this.redactorForm2.get('data1').setValidators(Validators.required);
      this.redactorForm2.get('data1').updateValueAndValidity();
    }
  }

  showInfoOfferType() {
    this.showInfo('', this.infoOfferTypeText);
  }

  showInfoHiddenPublished() {
    this.showInfo('', this.infoHiddenPublishedText);
  }

  showInfo(header: string, text: string) {
    const modalRef = this.openModalWindow(ModalInfoComponent);
    modalRef.componentInstance.header = header;
    modalRef.componentInstance.text = text;
  }

  goToStep(stepNumber: number, saveDraft: boolean) {
    if (!this.offerPublished && this.currentStep !== stepNumber) {
      let allowStep = true;
      if (this.currentStep === 1) {
        if (!this.redactorForm1.valid) {
          allowStep = false;
          const modalRef = this.openModalWindow(ModalErrorComponent);
          modalRef.componentInstance.errors = [this.offerError];
        } else {
          this.offer.typeId = this.form1.typeId.value;
        }
      }
      if (this.currentStep === 2) {
        if (!this.redactorForm2.valid && stepNumber !== 1) {
          allowStep = false;
          const modalRef = this.openModalWindow(ModalErrorComponent);
          modalRef.componentInstance.errors = [this.offerError];
        } else {
          this.offer.name = this.form2.name.value;
          this.offer.problem = this.form2.problem.value;
          this.offer.reason = this.form2.reason.value;
          this.offer.solution = this.form2.solution.value;
          this.offer.outlay = this.form2.outlay.value;
          this.offer.comment = this.form2.comment.value;
          this.offer.result = this.form2.result.value;
          this.offer.hasEconomicEffect = this.form2.hasEconomicEffect.value;
          this.offer.agreeWithSuggestionsPolicy = this.form2.agreeWithSuggestionsPolicy.value;
          this.offer.data1 = this.form2.data1?.value;
        }
      }
      if (this.currentStep === 3) {        
        if (!this.redactorForm3.valid && stepNumber !== 2 && stepNumber !== 1) {
          allowStep = false;
          const modalRef = this.openModalWindow(ModalErrorComponent);
          modalRef.componentInstance.errors = [this.offerError];
        } else {
          this.offer.members = this.offer.members.map((x) =>
            typeof x.partInProject === 'number' ? x : { ...x, partInProject: 0 }
          );

          const membersRight: boolean =
            this.offer.members.filter(
              (item) => !item.isAuthor && !item.isCoAuthor && !item.isRealizer
            ).length === 0;

          const reducer = (accumulator, curr) => accumulator + curr;
          const membersPartInProject: number[] = this.offer.members.map((x) => x.partInProject);

          if (membersPartInProject.reduce(reducer) !== 100) {
            allowStep = false;
            const modalRef = this.openModalWindow(ModalErrorComponent);
            modalRef.componentInstance.errors = [this.offerMembersPartInProjectError];
          }
          if (!membersRight) {
            allowStep = false;
            const modalRef = this.openModalWindow(ModalErrorComponent);
            modalRef.componentInstance.errors = [this.offerMembersError];
          }
          else {
            this.offer.organizationId = this.form3.organizationId.value;
            this.offer.departmentId = this.form3.departmentId.value;
            this.offer.divisionId = this.form3.divisionId.value;
            this.offer.userBlockId = this.form3.userBlockId.value;

            if (this.form3.rubric?.value) {
              this.offer.rubricIds = [this.form3.rubric.value];
            }

            if (this.form3.classificator?.value) {
              this.offer.offerClassificatorId = this.form3.classificator.value;
            }

            if (this.form3.lossTypes?.value) {
              this.offer.lossTypeIds = this.form3.lossTypes.value;
            }
          }
        }
      }

      if (allowStep) {
        if (saveDraft) {
          this.saveDraft = true;
          if (!this.offer.statusId) {
            this.save(this.offer.statusId);
          } else {
            this.validateAndSave(this.offer.statusId);
          }
        } else {
          if (stepNumber === 4) {
            this.offer.published = true;
            this.validateAndSave(1);
          } else {
            this.currentStep = stepNumber;
            this.utilsService.scrollToTop();
          }
        }
      }
    }
  }

  validateAndSave(statusId: number) {
    if (this.redactorForm1.valid && this.redactorForm2.valid && this.redactorForm3.valid) {
      this.save(statusId);
    }
    else {
      const modalRef = this.openModalWindow(ModalErrorComponent);
      modalRef.componentInstance.errors = [this.offerError];
    }
  }

  close() {
    const modalRef = this.openModalWindow(ModalSaveOfferComponent);
    modalRef.componentInstance.offerStatusId = this.offer.statusId;
    modalRef.componentInstance.passEntry.subscribe((result: boolean) => {
      if (result) {
        this.goToStep(this.currentStep + 1, true);
      } else {
        if (this.offer.id && !this.offer.statusId) {
          this.offerService.delete(this.offer.id).subscribe(() => {
            this.router.navigate(['/']);
          });
        } else {
          this.router.navigate(['/']);
        }
      }
    });
  }

  save(statusId: number) {
    this.offer.statusId = statusId;

    if (this.offer.typeId === this.settings.nornickelProblem_OfferKindId) {
      this.offer.result = this.offer.comment;
      this.offer.comment = "";
    }

    const formData = new FormData();

    //Очищаем из оффера файлы и их содержимое, что бы не отправлять их в json в base64
    //однако перекладываем новые файлы в FormData (IFormFile)
    var withoutFilesOffer: OfferDetails = { ...this.offer };
    
    withoutFilesOffer.photosBeforeIntroduction.filter(q => q.selectedFile).forEach(q => formData.append('image', q.selectedFile));
    withoutFilesOffer.photosBeforeIntroduction = withoutFilesOffer.photosBeforeIntroduction
      .filter(q => !q.selectedFile)
      .map(q => {
        q.fileContent = null;
        return q;
      });

    withoutFilesOffer.photosAfterIntroduction.filter(q => q.selectedFile).forEach(q => formData.append('file', q.selectedFile));
    withoutFilesOffer.photosAfterIntroduction = withoutFilesOffer.photosAfterIntroduction
      .filter(q => !q.selectedFile)
      .map(q => {
        q.fileContent = null;
        return q;
      });

    formData.append('offer', JSON.stringify(withoutFilesOffer));

    this.offerService.save(formData).subscribe((result) => {
      if (result.result) {
        this.offerPublished = true;
        this.currentStep = 4;
        this.utilsService.scrollToTop();
      } else {
        this.offer.statusId = null;
        const modalRef = this.openModalWindow(ModalErrorComponent);
        modalRef.componentInstance.errors = [this.offerSaveError];
      }
    });
  }

  numberOnly(event): boolean {
    const charCode = event.which ? event.which : event.keyCode;
    if (charCode > 31 && (charCode < 48 || charCode > 57)) {
      return false;
    }
    return true;
  }

  openModalWindow(content: any) {
    const modalRef = this.modalService.open(content, { windowClass: 'custom-position-modal' });
    return modalRef;
  }
}
